import { organizationGenerationLoginLink } from "@dutyon/apis";
import { AnalyticsTracker, AuthGuard, RouteErrorBoundary } from "@dutyon/ui-kit";
import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { AnalyticsReportPage } from "./pages/analytics/AnalyticsReports";
import { EmailSignUpPage } from "./pages/auth/EmailSignUpPage";
import { LoginPage } from "./pages/auth/LoginPage";
import { CompanyCreatePage } from "./pages/company/CompanyCreatePage";
import { CompanyDetailPage } from "./pages/company/CompanyDetailPage";
import { CompanyListPage } from "./pages/company/CompanyListPage";
import { CompanyUpdatePage } from "./pages/company/CompanyUpdatePage";
import { DutyRequestDetailPage } from "./pages/duty-request/DutyRequestDetailPage";
import { DutyRequestListPage } from "./pages/duty-request/DutyRequestListPage";
import { NotFoundPage } from "./pages/error/NotFoundPage";
import { GenerationJobPage } from "./pages/GenerationJobPage";
import { GenerationNotificationsPage } from "./pages/generation-notifications/GenerationNotificationsPage";
import { GpuHostPage } from "./pages/gpu-host/GpuHostPage";
import { HolidayListPage } from "./pages/holiday/HolidayListPage";
import { HomePage } from "./pages/home/HomePage";
import { InquiryDetailPage } from "./pages/inquiry/InquiryDetailPage";
import { InquiryListPage } from "./pages/inquiry/InquiryListPage";
import { NoticeCreatePage } from "./pages/notice/NoticeCreatePage";
import { NoticeEditPage } from "./pages/notice/NoticeEditPage";
import { NoticeListPage } from "./pages/notice/NoticeListPage";
import { ProDataDeletionListPage } from "./pages/pro-data-deletion/ProDataDeletionListPage";
import { ProRefundDetailPage } from "./pages/pro-refund/ProRefundDetailPage";
import { ProRefundListPage } from "./pages/pro-refund/ProRefundListPage";
import { DutyGenerationStatisticsPage } from "./pages/statistics/DutyGenerationStatisticsPage";
import { DutyRequestStatisticsPage } from "./pages/statistics/DutyRequestStatisticsPage";
import { UserTrendStatisticsPage } from "./pages/statistics/UserTrendStatisticsPage";
import { TermsCreatePage } from "./pages/terms/TermsCreatePage";
import { TermsEditPage } from "./pages/terms/TermsEditPage";
import { TermsListPage } from "./pages/terms/TermsListPage";
import { TrialApplicationDetailPage } from "./pages/trial-application/TrialApplicationDetailPage";
import { TrialApplicationListPage } from "./pages/trial-application/TrialApplicationListPage";
import { UserDetailPage } from "./pages/user/UserDetailPage";
import { UserListPage } from "./pages/user/UserListPage";

function BackofficeAuthGuard() {
  const location = useLocation();
  return <AuthGuard fallbackPath={organizationGenerationLoginLink(location.pathname)} />;
}

export const routes: RouteObject[] = [
  {
    element: <AnalyticsTracker />,
    errorElement: <RouteErrorBoundary homePath="/home" />,
    children: [
      {
        path: "/signup",
        element: <EmailSignUpPage />,
      },
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        element: <BackofficeAuthGuard />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { path: "/", element: <Navigate to="/home" replace /> },
              { path: "/home", element: <HomePage /> },
              { path: "/analytics/:reportSlug", element: <AnalyticsReportPage /> },
              { path: "/generation-jobs/:jobId", element: <GenerationJobPage /> },
              { path: "/gpu-host", element: <GpuHostPage /> },
              { path: "/generation-notifications", element: <GenerationNotificationsPage /> },
              { path: "/duty-requests", element: <DutyRequestListPage /> },
              { path: "/duty-requests/:id", element: <DutyRequestDetailPage /> },
              { path: "/users", element: <UserListPage /> },
              { path: "/users/:id", element: <UserDetailPage /> },
              { path: "/companies", element: <CompanyListPage /> },
              { path: "/companies/create", element: <CompanyCreatePage /> },
              { path: "/companies/:id", element: <CompanyDetailPage /> },
              { path: "/companies/:id/edit", element: <CompanyUpdatePage /> },
              { path: "/notices/app", element: <NoticeListPage target="APP" /> },
              { path: "/notices/app/new", element: <NoticeCreatePage target="APP" /> },
              { path: "/notices/app/:id/edit", element: <NoticeEditPage target="APP" /> },
              { path: "/notices/dashboard", element: <NoticeListPage target="DASHBOARD" /> },
              { path: "/notices/dashboard/new", element: <NoticeCreatePage target="DASHBOARD" /> },
              {
                path: "/notices/dashboard/:id/edit",
                element: <NoticeEditPage target="DASHBOARD" />,
              },
              { path: "/inquiries", element: <InquiryListPage /> },
              { path: "/pro-data-deletion", element: <ProDataDeletionListPage /> },
              { path: "/pro-billing/refunds", element: <ProRefundListPage /> },
              { path: "/pro-billing/refunds/:id", element: <ProRefundDetailPage /> },
              { path: "/inquiries/:id", element: <InquiryDetailPage /> },
              { path: "/holidays", element: <HolidayListPage /> },
              { path: "/trial-applications", element: <TrialApplicationListPage /> },
              { path: "/trial-applications/:id", element: <TrialApplicationDetailPage /> },
              { path: "/terms", element: <TermsListPage /> },
              { path: "/terms/new", element: <TermsCreatePage /> },
              { path: "/terms/:id/edit", element: <TermsEditPage /> },
              {
                path: "/statistics",
                element: <Navigate to="/statistics/duty-generation" replace />,
              },
              { path: "/statistics/duty-generation", element: <DutyGenerationStatisticsPage /> },
              { path: "/statistics/duty-requests", element: <DutyRequestStatisticsPage /> },
              { path: "/statistics/user-trends", element: <UserTrendStatisticsPage /> },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];
